import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface UserProfile {
  id: string;
  nickname: string;
  email: string;
  preferred_game: string | null;
  role: string;
  created_at: string;
  commission_rate?: number | null;
}

export const useUsersAdmin = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('🔍 [useUsersAdmin] Iniciando busca de usuários...');
      
      // Primeiro buscar todos os perfis
      console.log('📋 [useUsersAdmin] Buscando perfis na tabela profiles...');
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, nickname, email, preferred_game, role, created_at')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('❌ [useUsersAdmin] Erro ao buscar profiles:', profilesError);
        console.error('❌ [useUsersAdmin] Código do erro:', profilesError.code);
        console.error('❌ [useUsersAdmin] Mensagem do erro:', profilesError.message);
        console.error('❌ [useUsersAdmin] Detalhes do erro:', profilesError.details);
        throw profilesError;
      }

      console.log('✅ [useUsersAdmin] Profiles encontrados:', profiles?.length || 0);
      console.log('📊 [useUsersAdmin] Amostra dos profiles:', profiles?.slice(0, 3));

      // Depois buscar informações de mentor para quem tem role = 'mentor'
      const mentorIds = profiles?.filter(p => p.role === 'mentor').map(p => p.id) || [];
      console.log('👨‍🏫 [useUsersAdmin] Mentores encontrados:', mentorIds.length);
      
      let mentorCommissions: { [key: string]: number } = {};

      if (mentorIds.length > 0) {
        console.log('💰 [useUsersAdmin] Buscando comissões dos mentores...');
        const { data: mentorInfo, error: mentorError } = await supabase
          .from('mentor_info')
          .select('profile_id, commission_rate')
          .in('profile_id', mentorIds);

        if (mentorError) {
          console.warn('⚠️ [useUsersAdmin] Erro ao buscar mentor_info (não crítico):', mentorError);
        } else if (mentorInfo) {
          console.log('✅ [useUsersAdmin] Informações de mentor encontradas:', mentorInfo.length);
          mentorCommissions = mentorInfo.reduce((acc, info) => {
            acc[info.profile_id] = info.commission_rate;
            return acc;
          }, {} as { [key: string]: number });
        }
      }

      // Mapear dados incluindo taxa de comissão para mentores
      const usersWithCommission = profiles?.map(profile => ({
        id: profile.id,
        nickname: profile.nickname || 'Usuário sem nome',
        email: profile.email || 'Email não informado',
        preferred_game: profile.preferred_game,
        role: profile.role || 'jogador',
        created_at: profile.created_at,
        commission_rate: mentorCommissions[profile.id] || null
      })) || [];

      console.log('✅ [useUsersAdmin] Usuários processados:', usersWithCommission.length);
      console.log('📊 [useUsersAdmin] Amostra dos usuários processados:', usersWithCommission.slice(0, 3));
      
      setUsers(usersWithCommission);
      
    } catch (error: any) {
      console.error('❌ [useUsersAdmin] Erro geral:', error);
      toast({
        title: 'Erro',
        description: `Erro ao carregar usuários: ${error.message || 'Erro desconhecido'}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      console.log('🏁 [useUsersAdmin] Busca finalizada');
    }
  };

  useEffect(() => {
    console.log('🚀 [useUsersAdmin] Hook inicializado');
    fetchUsers();
  }, []);

  const openEditDialog = (user: UserProfile) => {
    setIsEditMode(true);
    setCurrentUser(user);
    setIsDialogOpen(true);
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      console.log(`🔄 [useUsersAdmin] Atualizando role do usuário ${userId} para ${newRole}`);
      
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      // Se tornar mentor, criar registro em mentor_info se não existir
      if (newRole === 'mentor') {
        console.log('👨‍🏫 [useUsersAdmin] Criando registro de mentor...');
        const { error: mentorError } = await supabase
          .from('mentor_info')
          .upsert({
            profile_id: userId,
            commission_rate: 0.2 // Taxa padrão de 20%
          });

        if (mentorError && mentorError.code !== '23505') { // Ignorar erro de duplicata
          console.error('⚠️ [useUsersAdmin] Erro ao criar mentor_info:', mentorError);
        }
      }

      toast({
        title: 'Sucesso',
        description: 'Função do usuário atualizada com sucesso',
      });

      // Recarregar lista
      await fetchUsers();
    } catch (error: any) {
      console.error('❌ [useUsersAdmin] Erro ao atualizar role:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar função do usuário',
        variant: 'destructive',
      });
    }
  };

  const filteredUsers = users.filter(user => 
    user.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log('🔍 [useUsersAdmin] Usuários filtrados:', filteredUsers.length, 'de', users.length);

  return {
    users: filteredUsers,
    loading,
    isDialogOpen,
    setIsDialogOpen,
    isEditMode,
    currentUser,
    searchTerm,
    setSearchTerm,
    openEditDialog,
    updateUserRole,
    refreshUsers: fetchUsers
  };
};
